import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { IMG } from "../data/images";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "../i18n";

const FILTER_KEYS = ["all", "food", "tandoor", "spices", "atmosphere"];
const FILTER_VALUES = ["All", "Food", "Tandoor", "Spices", "Atmosphere"];

const ITEMS = [
    { src: IMG.butterChicken, cat: "Food", alt: "Butter chicken" },
    { src: IMG.thali, cat: "Food", alt: "Indian thali spread" },
    { src: IMG.tandoori, cat: "Tandoor", alt: "Tandoori grill" },
    { src: IMG.spices, cat: "Spices", alt: "Aromatic Indian spices" },
    { src: IMG.interior1, cat: "Atmosphere", alt: "Restaurant atmosphere" },
    { src: IMG.biryani, cat: "Food", alt: "Fragrant biryani" },
    { src: IMG.samosa, cat: "Food", alt: "Crisp samosas" },
    { src: IMG.interior2, cat: "Atmosphere", alt: "Evening dining room" },
    { src: IMG.paneer, cat: "Food", alt: "Paneer curry" },
    { src: IMG.curryBread, cat: "Food", alt: "Curry with fresh bread" },
    { src: IMG.interior3, cat: "Atmosphere", alt: "Warm dining space" },
    { src: IMG.plating, cat: "Food", alt: "Elegant Indian plating" },
];

export const Gallery = () => {
    const { t } = useLang();
    const [filter, setFilter] = useState("All");
    const [lightbox, setLightbox] = useState(null);
    const visible = filter === "All" ? ITEMS : ITEMS.filter((i) => i.cat === filter);

    const step = useCallback(
        (dir) => {
            setLightbox((cur) => (cur === null ? null : (cur + dir + visible.length) % visible.length));
        },
        [visible.length],
    );

    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, step]);

    return (
        <section id="gallery" data-testid="gallery-section" className="relative py-24 sm:py-32 bg-onyx/40">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal className="mb-10">
                    <Overline>{t("gallery.overline")}</Overline>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">
                        {t("gallery.h1a")} <span className="italic gold-text">{t("gallery.h1b")}</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.1} className="flex gap-2.5 overflow-x-auto hide-scrollbar pb-2 mb-10">
                    {FILTER_KEYS.map((k, idx) => (
                        <button
                            key={k}
                            data-testid={`gallery-filter-${k}`}
                            onClick={() => {
                                setFilter(FILTER_VALUES[idx]);
                                setLightbox(null);
                            }}
                            className={`shrink-0 rounded-full px-5 py-2 text-xs tracking-[0.15em] uppercase transition-[background-color,color,border-color] duration-300 border ${
                                filter === FILTER_VALUES[idx]
                                    ? "bg-saffron text-ink border-saffron font-semibold"
                                    : "gold-border text-stone-300 hover:border-saffron/50 hover:text-saffron-glow"
                            }`}
                        >
                            {t(`gallery.filters.${k}`)}
                        </button>
                    ))}
                </Reveal>

                <div className="columns-2 md:columns-3 gap-4 sm:gap-5 [column-fill:balance]">
                    {visible.map((item, i) => (
                        <motion.button
                            key={item.src + item.alt}
                            data-testid={`gallery-item-${i}`}
                            layout
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.04 * i }}
                            onClick={() => setLightbox(i)}
                            className="group relative mb-4 sm:mb-5 w-full rounded-2xl overflow-hidden gold-border block break-inside-avoid"
                        >
                            <img
                                src={item.src}
                                alt={item.alt}
                                loading="lazy"
                                className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/35 transition-colors duration-500 flex items-end p-4">
                                <span className="text-xs tracking-[0.2em] uppercase text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    {item.cat}
                                </span>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {lightbox !== null && visible[lightbox] && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10"
                        onClick={() => setLightbox(null)}
                        role="dialog"
                        aria-label="Image viewer"
                    >
                        <motion.img
                            key={visible[lightbox].src}
                            src={visible[lightbox].src.replace("w=900", "w=1600").replace("w=1000", "w=1600").replace("w=1200", "w=1600")}
                            alt={visible[lightbox].alt}
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="max-h-[82vh] max-w-full rounded-2xl gold-border object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            data-testid="lightbox-close"
                            onClick={() => setLightbox(null)}
                            className="absolute top-5 right-5 w-11 h-11 rounded-full gold-border bg-ink/60 flex items-center justify-center text-cream hover:border-saffron/60 transition-colors duration-300"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); step(-1); }}
                            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full gold-border bg-ink/60 flex items-center justify-center text-cream hover:border-saffron/60 transition-colors duration-300"
                            aria-label="Previous image"
                        >
                            <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="lightbox-next"
                            onClick={(e) => { e.stopPropagation(); step(1); }}
                            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full gold-border bg-ink/60 flex items-center justify-center text-cream hover:border-saffron/60 transition-colors duration-300"
                            aria-label="Next image"
                        >
                            <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                        <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs tracking-[0.25em] uppercase text-stone-400">
                            {visible[lightbox].alt} · {lightbox + 1} / {visible.length}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
